import { AlertTriangle, CheckCircle, Upload } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UploadModal } from '@/components/documents/UploadModal';
import { renderPdfThumbnail } from '@/lib/pdfThumbnail';
import { useDocumentStore } from '@/store/documentStore';

import { Checkbox, Input, Label, SectionTitle } from './FormComponents';

import type { Licensing, FloridaLicensing, NewYorkLicensing, CommonCertifications } from '../../types/application';
import type { DocumentItem } from '@/types/document.types';

export type StepCErrors = Partial<Record<string, string>>;
export type StepCWarnings = Partial<Record<string, string>>;

interface StepCLicensingProps {
  value: Licensing;
  state: string; // From stepA - FL, NY, etc.
  errors: StepCErrors;
  warnings: StepCWarnings;
  onChange: (patch: Partial<Licensing>) => void;
  licensingUploads: Partial<Record<LicenseDocType, string>>;
  onLicensingUpload: (type: LicenseDocType, thumbUrl: string) => void;
  resolveDocumentApplicationId: () => Promise<string | null>;
}

const DEFAULT_CERTIFICATIONS: CommonCertifications = {
  hasCprAed: false,
  hasTwic: false,
};

export type LicenseDocType = 'class_d_license' | 'class_g_license' | 'guard_card' | 'firearm_credentials';

function buildDocumentItem(docType: LicenseDocType, name: string): DocumentItem {
  return {
    category: 'licensing',
    type: docType,
    name,
    description: name,
    isRequired: false,
    status: 'pending',
  };
}

export function StepCLicensing({
  value,
  state,
  errors,
  warnings,
  onChange,
  licensingUploads,
  onLicensingUpload,
  resolveDocumentApplicationId,
}: StepCLicensingProps) {
  const { t } = useTranslation();

  const uploadDocument = useDocumentStore((s) => s.uploadDocument);

  const [activeUploadType, setActiveUploadType] = useState<LicenseDocType | null>(null);

  const isFlorida = state === 'FL';
  const isNewYork = state === 'NY';

  // Ensure certifications has a default value for backward compatibility
  const certifications = value.certifications ?? DEFAULT_CERTIFICATIONS;

  const updateFlorida = (patch: Partial<FloridaLicensing>) => {
    onChange({
      florida: {
        hasClassD: false,
        hasClassG: false,
        ...value.florida,
        ...patch,
      },
    });
  };

  const updateNewYork = (patch: Partial<NewYorkLicensing>) => {
    onChange({
      newYork: {
        hasGuardCard: false,
        has8HourCert: false,
        has16HourCert: false,
        hasFirearmCreds: false,
        ...value.newYork,
        ...patch,
      },
    });
  };

  const updateCertifications = (patch: Partial<CommonCertifications>) => {
    onChange({
      certifications: {
        ...certifications,
        ...patch,
      },
    });
  };

  const handleNotApplicable = (checked: boolean) => {
    onChange({
      ...value,
      licensingNotApplicable: checked,
      florida: checked ? undefined : value.florida,
      newYork: checked ? undefined : value.newYork,
      certifications: checked ? DEFAULT_CERTIFICATIONS : value.certifications,
    });
  };

  const handleUpload = async (docType: LicenseDocType, name: string, file: File): Promise<boolean> => {
    const applicationId = await resolveDocumentApplicationId();
    if (!applicationId) {
      useDocumentStore.setState({
        applicationId: null,
        isUploading: null,
        uploadPhase: null,
        uploadProgress: 0,
        uploadError: t('documents.upload.error'),
      });
      return false;
    }
    useDocumentStore.setState({ applicationId });

    const item = buildDocumentItem(docType, name);
    const success = await uploadDocument(item, file);
    if (success) {
      // P2: use renderPdfThumbnail for PDFs; createObjectURL produces a broken <img> for raw PDF blobs
      const thumbUrl =
        file.type === 'application/pdf'
          ? ((await renderPdfThumbnail(file)) ?? URL.createObjectURL(file))
          : URL.createObjectURL(file);
      onLicensingUpload(docType, thumbUrl);
    }
    return success;
  };

  const hasWarnings = Object.keys(warnings).length > 0;

  return (
    <div className='space-y-6'>
      <SectionTitle title={t('wizard.stepC.title')} subtitle={t('wizard.stepC.subtitle')} />

      {/* N/A option */}
      <div className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
        <label className='flex items-start gap-3 cursor-pointer'>
          <input
            type='checkbox'
            checked={value.licensingNotApplicable ?? false}
            onChange={(e) => handleNotApplicable(e.target.checked)}
            className='mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'
          />
          <span className='flex flex-col gap-0.5'>
            <span className='text-sm font-medium text-amber-900'>{t('wizard.stepC.notApplicable')}</span>
            <span className='text-xs text-amber-700'>{t('wizard.stepC.notApplicableHint')}</span>
          </span>
        </label>
      </div>

      {!value.licensingNotApplicable && (
        <>
          {/* State-specific alerts */}
          {isFlorida ? (
            <div className='bg-blue-50 border-l-4 border-blue-500 p-4'>
              <div className='flex'>
                <AlertTriangle className='h-5 w-5 text-blue-500 mr-2 flex-shrink-0' />
                <p className='text-sm text-blue-700'>{t('wizard.stepC.floridaAlert')}</p>
              </div>
            </div>
          ) : null}

          {isNewYork ? (
            <div className='bg-blue-50 border-l-4 border-blue-500 p-4'>
              <div className='flex'>
                <AlertTriangle className='h-5 w-5 text-blue-500 mr-2 flex-shrink-0' />
                <p className='text-sm text-blue-700'>{t('wizard.stepC.newYorkAlert')}</p>
              </div>
            </div>
          ) : null}

          {errors.licenses ? (
            <div className='bg-red-50 border-l-4 border-red-500 p-3 text-sm text-red-700'>{errors.licenses}</div>
          ) : null}
          {hasWarnings ? (
            <div className='bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-800'>
              {t('wizard.validation.warningBanner')}
            </div>
          ) : null}

          {/* Florida Licensing */}
          {isFlorida ? (
            <div className='border rounded-lg p-4 space-y-4'>
              <h3 className='font-semibold text-gray-900'>{t('wizard.stepC.floridaLicenses')}</h3>

              {/* Class D */}
              <div className='space-y-3'>
                <Checkbox
                  label={t('wizard.stepC.hasClassD')}
                  checked={value.florida?.hasClassD ?? false}
                  onChange={(checked) => updateFlorida({ hasClassD: checked })}
                  error={errors.hasClassD}
                />
                {errors.hasClassD ? <p className='text-xs text-red-600 mt-1 ml-6'>{errors.hasClassD}</p> : null}
                {value.florida?.hasClassD ? (
                  <div className='ml-6 space-y-3'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div>
                        <Label required>{t('wizard.stepC.licenseNumber')}</Label>
                        <Input
                          value={value.florida?.classDNumber ?? ''}
                          onChange={(e) => updateFlorida({ classDNumber: e.target.value })}
                          placeholder='D-1234567'
                          error={errors.classDNumber}
                        />
                      </div>
                      <div>
                        <Label required>{t('wizard.stepC.expirationDate')}</Label>
                        <Input
                          type='date'
                          value={value.florida?.classDExpiration ?? ''}
                          onChange={(e) => updateFlorida({ classDExpiration: e.target.value })}
                          error={errors.classDExpiration}
                          warning={warnings.classDExpiration}
                        />
                      </div>
                    </div>
                    <UploadButton
                      docType='class_d_license'
                      label={t('wizard.stepC.uploadPhoto')}
                      uploadedLabel={t('wizard.stepC.photoUploaded')}
                      thumbUrl={licensingUploads['class_d_license']}
                      onOpen={() => setActiveUploadType('class_d_license')}
                    />
                  </div>
                ) : null}
              </div>

              {/* Class G (Armed) */}
              <div className='space-y-3'>
                <Checkbox
                  label={t('wizard.stepC.hasClassG')}
                  checked={value.florida?.hasClassG ?? false}
                  onChange={(checked) => updateFlorida({ hasClassG: checked })}
                  error={errors.hasClassG}
                />
                {errors.hasClassG ? <p className='text-xs text-red-600 mt-1 ml-6'>{errors.hasClassG}</p> : null}
                {value.florida?.hasClassG ? (
                  <div className='ml-6 space-y-3'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div>
                        <Label required>{t('wizard.stepC.licenseNumber')}</Label>
                        <Input
                          value={value.florida?.classGNumber ?? ''}
                          onChange={(e) => updateFlorida({ classGNumber: e.target.value })}
                          placeholder='G-1234567'
                          error={errors.classGNumber}
                        />
                      </div>
                      <div>
                        <Label required>{t('wizard.stepC.expirationDate')}</Label>
                        <Input
                          type='date'
                          value={value.florida?.classGExpiration ?? ''}
                          onChange={(e) => updateFlorida({ classGExpiration: e.target.value })}
                          error={errors.classGExpiration}
                          warning={warnings.classGExpiration}
                        />
                      </div>
                    </div>
                    <UploadButton
                      docType='class_g_license'
                      label={t('wizard.stepC.uploadPhoto')}
                      uploadedLabel={t('wizard.stepC.photoUploaded')}
                      thumbUrl={licensingUploads['class_g_license']}
                      onOpen={() => setActiveUploadType('class_g_license')}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* New York Licensing */}
          {isNewYork ? (
            <div className='border rounded-lg p-4 space-y-4'>
              <h3 className='font-semibold text-gray-900'>{t('wizard.stepC.newYorkLicenses')}</h3>

              {/* Guard Card */}
              <div className='space-y-3'>
                <Checkbox
                  label={t('wizard.stepC.hasGuardCard')}
                  checked={value.newYork?.hasGuardCard ?? false}
                  onChange={(checked) => updateNewYork({ hasGuardCard: checked })}
                  error={errors.hasGuardCard}
                />
                {errors.hasGuardCard ? <p className='text-xs text-red-600 mt-1 ml-6'>{errors.hasGuardCard}</p> : null}
                {value.newYork?.hasGuardCard ? (
                  <div className='ml-6 space-y-3'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div>
                        <Label required>{t('wizard.stepC.cardNumber')}</Label>
                        <Input
                          value={value.newYork?.guardCardNumber ?? ''}
                          onChange={(e) => updateNewYork({ guardCardNumber: e.target.value })}
                          placeholder='NY-9876543'
                          error={errors.guardCardNumber}
                        />
                      </div>
                      <div>
                        <Label required>{t('wizard.stepC.expirationDate')}</Label>
                        <Input
                          type='date'
                          value={value.newYork?.guardCardExpiration ?? ''}
                          onChange={(e) => updateNewYork({ guardCardExpiration: e.target.value })}
                          error={errors.guardCardExpiration}
                          warning={warnings.guardCardExpiration}
                        />
                      </div>
                    </div>
                    <UploadButton
                      docType='guard_card'
                      label={t('wizard.stepC.uploadPhoto')}
                      uploadedLabel={t('wizard.stepC.photoUploaded')}
                      thumbUrl={licensingUploads['guard_card']}
                      onOpen={() => setActiveUploadType('guard_card')}
                    />
                  </div>
                ) : null}
              </div>

              {/* 8-Hour Certificate */}
              <div className='space-y-3'>
                <Checkbox
                  label={t('wizard.stepC.has8HourCert')}
                  checked={value.newYork?.has8HourCert ?? false}
                  onChange={(checked) => updateNewYork({ has8HourCert: checked })}
                />
                {value.newYork?.has8HourCert ? (
                  <div className='ml-6'>
                    <Label>{t('wizard.stepC.completionDate')}</Label>
                    <Input
                      type='date'
                      value={value.newYork?.cert8HourDate ?? ''}
                      onChange={(e) => updateNewYork({ cert8HourDate: e.target.value })}
                      className='max-w-xs'
                    />
                  </div>
                ) : null}
              </div>

              {/* 16-Hour Certificate */}
              <div className='space-y-3'>
                <Checkbox
                  label={t('wizard.stepC.has16HourCert')}
                  checked={value.newYork?.has16HourCert ?? false}
                  onChange={(checked) => updateNewYork({ has16HourCert: checked })}
                />
                {value.newYork?.has16HourCert ? (
                  <div className='ml-6'>
                    <Label>{t('wizard.stepC.completionDate')}</Label>
                    <Input
                      type='date'
                      value={value.newYork?.cert16HourDate ?? ''}
                      onChange={(e) => updateNewYork({ cert16HourDate: e.target.value })}
                      className='max-w-xs'
                    />
                  </div>
                ) : null}
              </div>

              {/* Firearm Credentials */}
              <div className='space-y-3'>
                <Checkbox
                  label={t('wizard.stepC.hasFirearmCreds')}
                  checked={value.newYork?.hasFirearmCreds ?? false}
                  onChange={(checked) => updateNewYork({ hasFirearmCreds: checked })}
                  error={errors.hasFirearmCreds}
                />
                {errors.hasFirearmCreds ? (
                  <p className='text-xs text-red-600 mt-1 ml-6'>{errors.hasFirearmCreds}</p>
                ) : null}
                {value.newYork?.hasFirearmCreds ? (
                  <div className='ml-6 space-y-3'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div>
                        <Label required>{t('wizard.stepC.credentialNumber')}</Label>
                        <Input
                          value={value.newYork?.firearmCredsNumber ?? ''}
                          onChange={(e) => updateNewYork({ firearmCredsNumber: e.target.value })}
                          error={errors.firearmCredsNumber}
                        />
                      </div>
                      <div>
                        <Label required>{t('wizard.stepC.expirationDate')}</Label>
                        <Input
                          type='date'
                          value={value.newYork?.firearmCredsExpiration ?? ''}
                          onChange={(e) => updateNewYork({ firearmCredsExpiration: e.target.value })}
                          error={errors.firearmCredsExpiration}
                          warning={warnings.firearmCredsExpiration}
                        />
                      </div>
                    </div>
                    <UploadButton
                      docType='firearm_credentials'
                      label={t('wizard.stepC.uploadPhoto')}
                      uploadedLabel={t('wizard.stepC.photoUploaded')}
                      thumbUrl={licensingUploads['firearm_credentials']}
                      onOpen={() => setActiveUploadType('firearm_credentials')}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Common Certifications */}
          <div className='border rounded-lg p-4 space-y-4'>
            <h3 className='font-semibold text-gray-900'>{t('wizard.stepC.commonCertifications')}</h3>

            {/* CPR/AED */}
            <div className='space-y-3'>
              <Checkbox
                label={t('wizard.stepC.hasCprAed')}
                checked={certifications.hasCprAed}
                onChange={(checked) => updateCertifications({ hasCprAed: checked })}
              />
              {certifications.hasCprAed ? (
                <div className='ml-6'>
                  <Label>{t('wizard.stepC.expirationDate')}</Label>
                  <Input
                    type='date'
                    value={certifications.cprAedExpiration ?? ''}
                    onChange={(e) => updateCertifications({ cprAedExpiration: e.target.value })}
                    className='max-w-xs'
                    warning={warnings.cprAedExpiration}
                  />
                </div>
              ) : null}
            </div>

            {/* TWIC */}
            <div className='space-y-3'>
              <Checkbox
                label={t('wizard.stepC.hasTwic')}
                checked={certifications.hasTwic}
                onChange={(checked) => updateCertifications({ hasTwic: checked })}
              />
              {certifications.hasTwic ? (
                <div className='ml-6'>
                  <Label>{t('wizard.stepC.expirationDate')}</Label>
                  <Input
                    type='date'
                    value={certifications.twicExpiration ?? ''}
                    onChange={(e) => updateCertifications({ twicExpiration: e.target.value })}
                    className='max-w-xs'
                    warning={warnings.twicExpiration}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}

      {/* Upload Modals */}
      {(['class_d_license', 'class_g_license', 'guard_card', 'firearm_credentials'] as LicenseDocType[]).map(
        (docType) => (
          <UploadModal
            key={docType}
            isOpen={activeUploadType === docType}
            onClose={() => setActiveUploadType(null)}
            onUpload={(file) => handleUpload(docType, docType, file)}
            documentType={docType}
            acceptedMimeTypes='image/*,.pdf'
            maxSizeMb={10}
          />
        ),
      )}
    </div>
  );
}

interface UploadButtonProps {
  docType: LicenseDocType;
  label: string;
  uploadedLabel: string;
  thumbUrl: string | undefined;
  onOpen: () => void;
}

function UploadButton({ label, uploadedLabel, thumbUrl, onOpen }: UploadButtonProps) {
  if (thumbUrl) {
    return (
      <div className='flex items-center gap-3 mt-1'>
        <img
          src={thumbUrl}
          alt=''
          aria-hidden='true'
          className='h-10 w-10 rounded object-cover border border-gray-200'
        />
        <span className='flex items-center gap-1.5 text-sm text-green-700 font-medium'>
          <CheckCircle size={15} />
          {uploadedLabel}
        </span>
        <button
          type='button'
          onClick={onOpen}
          className='ml-auto flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium transition'>
          <Upload size={13} />
          {label}
        </button>
      </div>
    );
  }

  return (
    <button
      type='button'
      onClick={onOpen}
      className='flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium transition mt-1'>
      <Upload size={15} />
      {label}
    </button>
  );
}
